import { IMessageProvider, IProgressProvider } from "../PA.Core/PA.Core.IF.Basic";
import { ISearch } from "./PA.UIM.Core.IF.Search";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";

export interface IUIFramework {
    readonly bIsFrameworkLoaded: boolean;
    readonly OnLoadComplete: Promise<void>;
    readonly ProgressProvider: IProgressProvider;
    readonly MessageProvider: IMessageProvider;

    readonly UIProfiles: IUIProfile[];
    readonly ApplicationSuiteTypes: IApplicationSuiteType[];
    readonly Reports: IModuleReport[];

    getApplicationSuite(anApplicationSuiteId: string | number): IApplicationSuite;
    readonly ApplicationSuites: IApplicationSuite[];

    getApplication(anApplicationId: string | number): IApplication;
    readonly Applications: IApplication[];

    getExecutable(anExecutableId: string | number): IExecutable;
    readonly Executables: IExecutable[];

    getAppModule(anAppId: string | number, anExecutableId: string | number): IApplicationModule;

    getExecutableAppModules(anExecutableId: string): { Application: IApplication; AppModule: IApplicationModule }[];
}

export interface IApplicationSuiteType {
    wb_id: number;
    id_ui: string;
    name: string;
    sort: number;
    renderOptions: { [id: string]: any };
}

export interface IUIProfile {
    wb_id: number;
    id_ui: string;
    name: string;
    sort: number;
    roles: { [id: string]: boolean };
    renderOptions: { [id: string]: any };
}

export interface IApplicationSuiteAppRel {
    application: IApplication;
    appName: string;
    appDesc: string;
    groupName: string;
    ui5Icon: string;
    launchPolicy: string;
    bAutoLaunch: boolean;
    sort: number;
    renderOptions: { [id: string]: any };
    appOverrideName: string;
}

export interface IApplicationSuite {
    wb_id: number;
    id_ui: string;
    name: string;
    description: string;
    sort: number;
    ui5Icon: string;
    type: IApplicationSuiteType;
    profile: IUIProfile;
    renderOptions: { [id: string]: any };
    bIsLandingPage: boolean;
    LandingPageHeaderImage: string;
    LandingPageHeaderImageSize: string;
    LandingPageAppHubBGColor: string;
    LandingPageFeedBGColor: string;

    applicationRels: IApplicationSuiteAppRel[];
    feed?: any[]
}

export enum LaunchPolicy {
    SingleInstance = "SingleInstance",
    MultiInstances = "MultiInstances",
}

export enum ExecutableType {
    UI5Component = "UI5Component",
    ReportTile = "ReportTile",
}

export enum PlacementPolicy {}

export enum ModuleBarPolicy {
    Hide = "Hide",
    Show = "Show", // Top Tab Bar
    ShowToolbar = "ShowToolbar" // Left Tool Bar
}

export enum TaskBarPolicy {
    Hide = "Hide",
    Show = "Show",
    ShowOnWhenNeeded = "ShowOnWhenNeeded",
}

export enum AppFinderPolicy {
    Hide = "Hide",
    Show = "Show",
}

export enum StartupPolicy {
    None = "None",
    AutoStart = "AutoStart",
    AutoStartSearch = "AutoStartSearch",
}

export enum EnterpriseLevel {
    None="None",
    MDSet="MDSet",
    Company="Company",
    SalesOrg="SalesOrg",
    SalesArea="SalesArea",
}

export enum ReportModeDefault {
    Basic = "Basic",
    Advanced = "Advanced",
    Analysis = "Analysis Studio",
}

export interface IApplication {
    wb_id: number;
    id_ui: string;
    name: string;
    appDesc:string;
    groupName: string;
    sort: number;

    keywords: string[];
    keywordDict: { [id: string]: boolean };

    startupPolicy: string;
    launchPolicy: string;
    moduleBarPolicy: string;
    taskBarPolicy: string;
    appfinderPolicy: string;
    reportModeDefault: string;

    ui5Icon: string;
    viewRoles: { [id: string]: boolean };

    renderOptions: { [id: string]: any };

    // Find
    applicationModules: IApplicationModule[];
    reports: IModuleReport[];
}

export interface IApplicationModule {
    //
    wb_id: number;
    name: string;
    shortName: string;
    startupPolicy: string;
    placementPolicy: string;
    mruPolicy: string;
    launchPolicy: string;
    sort: number;
    comments: string;

    viewRoles: { [id: string]: boolean };
    editRoles: { [id: string]: boolean };

    executable: IExecutable;
    executableParams: { [id: string]: any };

    ui5Icon: string;
    ui5IconColor: string;
    renderOptions: { [id: string]: any };
}

export interface IExecutable {
    // Current Application Manager level
    wb_id: number;
    id_ui: string;
    name: string;
    help_url: string;
    type: string; // UI5, Dashboard, ..
    enterpriseLevel: EnterpriseLevel;

    viewRoles: { [id: string]: boolean };
    editRoles: { [id: string]: boolean };

    boClass: new (options?: any) => IBusinessObject;
    boClassName: string;
    boCreateOptions: { [id: string]: any };
    boCreateNewOptions: { [id: string]: any };
    boCreateNewWizard: () => any;
    boAfterCreateNewWizard: () => any;
    boCreateNewWizardId: string;
    boAfterCreateNewWizardId: string;
    boCopyOptions: { [id: string]: any };
    boCopyWizard: () => any;
    boCopyWizardId: string;
    boAfterCopyWizard: () => any;
    boAfterCopyWizardId: string;
    boLoadOptions: { [id: string]: any };
    boCreateNewOnSessionCreate: boolean;

    searchClass: ISearch;
    searchOptions: { [id: string]: any };

    renderOptions: { [id: string]: any };

    ui5ResourceRoot: string;
    ui5ResourceRootPath: string;
    ui5ComponentId: string;
    ui5Icon: string;

    reportId: number;

    headerBarShowInd: boolean;
    ui5HeaderBarId: string;
    footerbarShowInd: boolean;
    ui5FooterbarId: string;
    parentToolbarHideInd: boolean;
    tbCreateNewShowInd: boolean;
    tbSearchShowInd: boolean;
    tbSaveShowInd: boolean;
    tbDeleteShowInd: boolean;
    tbCloseShowInd: boolean;
    tbCopyShowInd: boolean;
    tbCreateNoteShowInd: boolean;
    tbIdDialogShowInd: boolean;
    tbReloadShowInd: boolean;
    tbFileListShowInd: boolean;
    bCacheBO: boolean;
}

export interface IModuleReport {
    wb_id: number;
    id_ui: string;
    name: string;
    treePath: string;
    module_id: string;
    sort: number;
    reportTypeId: string;
    renderOptions: { [id: string]: any };
}
